import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  UserIcon,
  AcademicCapIcon,
  MapPinIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { checkProfileCompletion, getProfileCompletionMessage, getFieldDisplayName } from '../utils/profileValidation';
import './ProfileCompletionAlert.css';

const ProfileCompletionAlert = ({ user, showFullDetails = false }) => {
  const profileCheck = checkProfileCompletion(user);
  const message = getProfileCompletionMessage(profileCheck);

  if (profileCheck.canApplyForCourses && !showFullDetails) {
    return null; // Don't show alert if profile is complete
  }

  return (
    <div className={`profile-completion-alert ${message.type}`}>
      <div className="alert-header">
        <div className="alert-icon-container">
          {message.type === 'success' ? (
            <CheckCircleIcon className="alert-icon success" />
          ) : (
            <ExclamationTriangleIcon className="alert-icon warning" />
          )}
        </div>
        <div className="alert-content">
          <h3 className="alert-title">{message.title}</h3>
          <p className="alert-message">{message.message}</p>
        </div>
      </div>

      {!profileCheck.canApplyForCourses && (
        <div className="completion-details">
          <div className="completion-sections">
            {/* Personal Information */}
            <div className="completion-section">
              <div className="section-header">
                <UserIcon className="section-icon" />
                <div className="section-info">
                  <h4 className="section-title">Personal Information</h4>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${profileCheck.completion.personal}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{profileCheck.completion.personal}% Complete</span>
                </div>
              </div>
              {profileCheck.completion.personal < 80 && profileCheck.missingForApplication.personal.length > 0 && (
                <div className="missing-fields">
                  <p className="missing-text">
                    Missing: {profileCheck.missingForApplication.personal.map(field => getFieldDisplayName(field)).join(', ')}
                  </p>
                </div>
              )}
            </div>

            {/* Education */}
            <div className="completion-section">
              <div className="section-header">
                <AcademicCapIcon className="section-icon" />
                <div className="section-info">
                  <h4 className="section-title">Education Background</h4>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${profileCheck.completion.education}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{profileCheck.completion.education}% Complete</span>
                </div>
              </div>
              {profileCheck.completion.education < 100 && profileCheck.missingForApplication.education.length > 0 && (
                <div className="missing-fields">
                  <p className="missing-text">
                    Missing: {profileCheck.missingForApplication.education.map(field => getFieldDisplayName(field)).join(', ')}
                  </p>
                </div>
              )}
            </div>

            {/* Location */}
            <div className="completion-section">
              <div className="section-header">
                <MapPinIcon className="section-icon" />
                <div className="section-info">
                  <h4 className="section-title">Location Services</h4>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${profileCheck.completion.location}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{profileCheck.completion.location}% Complete</span>
                </div>
              </div>
              {profileCheck.completion.location < 100 && profileCheck.missingForApplication.location.length > 0 && (
                <div className="missing-fields">
                  <p className="missing-text">
                    Missing: {profileCheck.missingForApplication.location.map(field => getFieldDisplayName(field)).join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="alert-actions">
            <Link to="/profile" className="complete-profile-btn">
              <UserIcon className="btn-icon" />
              Complete Profile
              <ArrowRightIcon className="btn-arrow" />
            </Link>
          </div>
        </div>
      )}

      {profileCheck.canApplyForCourses && showFullDetails && (
        <div className="completion-success">
          <div className="success-message">
            <CheckCircleIcon className="success-icon" />
            <div>
              <h4 className="success-title">Profile Complete!</h4>
              <p className="success-text">You can now apply for any available course.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCompletionAlert;
